import type { KeldaClaimingRequestDto, KeldaRequestCreationDto, KeldaRequestGetResponseDto, KeldaRequestUpdateDto, KeldaRequestUpdateResponseDto } from '@mimir/common/dto/keldaRequestDtos';
import { api_getKeldaRequest, api_deleteKeldaRequest, api_createKeldaRequest, api_updateKeldaRequest, api_getKeldaWorkerConfig, api_getKeldaSupervisorConfig, api_updateOrCreateKeldaStatus, api_claimKeldaRequest, api_getKeldaS3ServerKeysPresignedURL, api_getWebsocketInfo } from '../api/keldaApi';
import { api_deleteItemUploadLock } from '@mimir/common/api/uploadApi';
import { api_getItemAncestry, api_getFolderContent, api_createFolder, api_deleteFolder, api_addItemToFolder } from '../api/folderApi';
import {
  api_createGeneratedMediaUploadUrl, api_deleteItem, api_getAttachmentsToItem, api_getAttachmentToItemByName, api_getExpectedLivePipelines, api_getItem, api_getItemFields, api_getItemRelations, api_getMdf, api_getMdfs, api_getMediaSegmentUrls, api_getNextMosMessage, api_getPackages, api_getRouter, api_getScheduledRecording, api_getSelectedDynamicSourceForPipeline, api_getTenantConfig, api_getTimedMetadata, api_logStatistic, api_logWarningOnItem, api_markTransferRequest, api_performLiveItemAction, api_postMosMessage, api_postQcEvents, api_postRecordLog, api_postRestoreMedia,
  api_postRouterMessage,
  api_getRouterSources,
  api_reportPipelineActionResult,
  api_appendRetrievedSegment,
  api_setSelectedDynamicSourceForPipeline,
  api_startBackendJob,
  api_undeleteItem,
  api_batchConcludeJobUploadURLs,
  api_batchCreateItems,
  api_batchSignTaskUploadURLs,
  api_batchUpdateItems,
  api_updateArchiveStatus,
  type GetItemRelationsParams,
  api_updateItem, api_updateItemRelations, api_updateKeldaMediaPipelineStatus, api_updateTimedMetadata, api_updateItemCustomIcon,
  api_batchGetItemsByExternalId,
  api_createItemReplacement,
  api_getExternalHighresInfo,
  api_getPendingOfflineStorageJobs,
  api_getStartedOfflineStorageJobs,
  api_updateOfflineStorageJob,
  api_getOfflineStoragePluginsForIntegrator,
  api_getItemOfflineStorageInfo,
} from './api';
import { GetFolderCommand } from '@mimir/common/api/folderCommands';
import { callApiCommand } from '../api/callApiCommand';
import type { LoadedItem, ItemUpdateRequestDto } from '@mimir/common/dto/searchResultHit';
import type { ItemCreationRequestDto } from '@mimir/common/dto/itemCreationRequestDto';
import type { ExtendedItemDto, ItemCustomIconUpdateDto } from '@mimir/common/dto/extendedItemDto';
import type { GeneratedMediaSignedUrlResponseDto } from '@mimir/common/dto/itemModelPrimitives';
import type { GetItemsDto, ItemCreationResponse, StartAnalyticsJobRequestDto } from '@mimir/common/dto/itemModels';
import { READABLE_MDF_FIELDS_QUERY_PARAMETER } from '@mimir/common/dto/itemModelPrimitives';
import type { BackendOps, GetExifEmbeddedUrlResponse } from './backendOperations';
import type { ItemMetaData } from '@mimir/common/dto/fieldValue';
import type { MdfEntity } from '@mimir/common/dto/mdfModels';
import type { RestoreMediaRequestDto } from '@mimir/common/dto/restoreMediaRequestDto';
import type { HttpQueryParameters, AdditionalParams } from '@mimir/common/util/apiClient';
import { apiGet, apiPost } from '@mimir/common/util/http';
import type { UpdateOrCreateKeldaStatusDto } from '@mimir/common/dto/statusDto';
import type { MosRequestFromNcsDto, MosResponseFromNcsDto, MosLockReleaseRequestDto } from '@mimir/common/dto/mosDtos';
import type { TimedMetadataCollection, UpdateTimedMetadataRequestDto } from '@mimir/common/dto/timedMetadataModels';
import type { ArchiveOperationRequestDto } from '@mimir/common/dto/archiveOperationDto';
import type { StreamRoleId } from '@mimir/common/dto/mediaAvailability';
import type { MarkTransferRequestDto } from '@mimir/common/dto/transferRequestDtos';
import type { RouterMessageDto } from '@mimir/common/dto/routerControllerDtos';
import type { LiveItemStateRequestDto, LiveRecordingItemStateDto, PipelineActionResultDto } from '@mimir/common/dto/liveDtos';
import type { LogStaticRequestDto } from '@mimir/common/dto/statsDtos';
import type { RelationType } from '@mimir/common/dto/relationsEntity';
import type { Relation } from '@mimir/common/dto/relationModels';
import type { ItemAttachmentWithDownloadUrlDto } from '@mimir/common/dto/itemAttachmentDto';
import type { QcEvent } from '@mimir/common/dto/qcDtos';
import type { SearchResult } from '@mimir/common/search/interfaces';
import type { FolderDto} from '@mimir/common/dto/folderModels';
import { FolderTypeName } from '@mimir/common/dto/folderModels';
import type { BackendClient } from '@mimir/common/util/backendClient';
import type { SearchProps} from './search';
import { buildSearchParams, getSearchResult } from './search';
import type { OptionListDto } from '@mimir/common/dto/optionListDto';
import { ofItem, ofOptionList } from '@mimir/common/aws/api/path';
import type { ItemAccessControl } from '@mimir/common/dto/itemAccessControlDto';
import type { DynamoItemType } from '@mimir/common/dto/itemTypes';
import type { ProgressInfo } from '@mimir/common/dto/progress';
import type { AnalyticConfig } from '@mimir/common/dto/tenantModel';
import { DEFAULT_MDF_ID, SystemDefaultField } from '@mimir/common/mdf/mdfDefaults';
import { convertAnalyticConfig } from '@mimir/common/util/item';
import { itemTypeFromFilename, mediaTypeFromFilename } from '@mimir/common/util/mediaContentType';
import type { PackageDefinition } from '@mimir/common/dto/keldaConfigPrimitives';
import { assumeResultArray, assumeResultObject } from '@mimir/common/client/assumeResult';
import type { GeoShape } from '@mimir/common/dto/GeoLocation';
import { getEmbed } from '@mimir/common/client/hal';
import type { PresignedURLMethodType } from '@mimir/common/dto/keldaS3ServerKeysTypes';
import { ItemMetadataUpdater } from '../metadata/itemMetadataUpdater';
import type { Logger } from '@mimir/common/types/logger';
import { invariant } from '@mimir/common/util/invariant';
import type { InitializedKeldaWebSocketDto } from '@mimir/common/dto/wsDtos';
import type { ItemWarningsDto } from '@mimir/common/dto/itemWarningsDto';
import type { UpdateKeldaMediaPipelineStatusDto } from '@mimir/common/dto/keldaMediaPipelineStatusDto';
import type { UpdateSelectedDynamicSourceDto } from '@mimir/common/dto/liveSourceDtos';
import type { BatchCreateItemsRequestDto } from '@mimir/common/dto/batchCreateItems';
import type { BatchUpdateItemsRequestDto } from '@mimir/common/dto/batchUpdateItems';
import type { BatchConcludeJobUploadInput, BatchConcludeJobUploadResponse, BatchSignJobUploadUrlsInput } from '@mimir/common/dto/jobUploadDtos';
import { api_getItems } from '../api/getItemsApi';
import type { BatchGetItemsByExternalIdRequestDto } from '@mimir/common/dto/batchGetItemsByExternalId';
import { GetEXIFEmbeddedImageURLCommand } from '@mimir/common/api/mdfToEXIFEmbeddedImageURLCommands';
import { sendApiCommand } from '@mimir/common/client/sendApiCommand';
import type { ItemVersionDto } from '@mimir/common/dto/itemVersionDto';
import type { ExternalHighresInfoDto, RetrievedSegmentRangeDto } from '@mimir/common/dto/externalHighresInfoDto';
import type { OfflineStorageJobUpdate } from '@mimir/common/dto/offlineStorageJobDto';


export class DefaultBackendOps implements BackendOps {

  constructor(private backendClient: BackendClient) {}

  async getWebsocketInfo(keldaId: string, keldaInstanceId: string): Promise<InitializedKeldaWebSocketDto[]> {
    return await api_getWebsocketInfo(this.backendClient, keldaId, keldaInstanceId);
  }

  async getKeldaRequest(requestId: string): Promise<KeldaRequestGetResponseDto> {
    return await api_getKeldaRequest(this.backendClient, requestId);
  }

  async deleteKeldaRequest(requestId: string): Promise<void> {
    await api_deleteKeldaRequest(this.backendClient, requestId);
  }

  async updateKeldaRequest(requestId: string, updatePayload: KeldaRequestUpdateDto): Promise<KeldaRequestUpdateResponseDto> {
    return await api_updateKeldaRequest(this.backendClient, requestId, updatePayload);
  }

  async updateItem(itemId: string, payload: ItemUpdateRequestDto): Promise<Partial<ExtendedItemDto>> {
    return await api_updateItem(this.backendClient, itemId, payload);
  }

  async updateItemCustomIcon(itemId: string, payload: ItemCustomIconUpdateDto): Promise<string | void> {
    return await api_updateItemCustomIcon(this.backendClient, itemId, payload);
  }
  async logWarningOnItem(itemId: string, warning: ItemWarningsDto): Promise<void> {
    return await api_logWarningOnItem(this.backendClient, itemId, warning);
  }

  async getItemFields<Key extends keyof LoadedItem>(itemId: string, includeFields: Readonly<Key[]>, readableMetadataFields = false): Promise<Pick<LoadedItem, Key>> {
    return await api_getItemFields(this.backendClient, itemId, includeFields, readableMetadataFields);
  }

  async getItemAttachmentDownloadUrl(itemId: string, attachmentFileName: string): Promise<string> {
    return await api_getAttachmentToItemByName(this.backendClient, itemId, attachmentFileName);
  }

  async getMdfByKey(mdfId: string): Promise<MdfEntity> {
    return await api_getMdf(this.backendClient, mdfId);
  }

  async getMdfs(): Promise<MdfEntity[]> {
    const result =  await api_getMdfs(this.backendClient, {
      includeFields: true,
      includeViews: false,
    });
    return getEmbed(result, 'collection', []);
  }

  async getKeldaS3ServerKeysPresignedURL(keldaId: string, method: PresignedURLMethodType) {
    const result = await api_getKeldaS3ServerKeysPresignedURL(this.backendClient, keldaId, { queryParams: { method } });
    return result;
  }

  async postRestoreMedia(itemId: string, payload: RestoreMediaRequestDto): Promise<any> {
    return await api_postRestoreMedia(this.backendClient, itemId, payload);
  }

  async claimKeldaRequest(body: KeldaClaimingRequestDto) {
    return await api_claimKeldaRequest(this.backendClient, body);
  }

  async createKeldaRequest(body: KeldaRequestCreationDto, additionalParams?: AdditionalParams) {
    return await api_createKeldaRequest(this.backendClient, body, additionalParams);
  }

  async createNewVersion(itemId: string, versionDto: ItemVersionDto) {
    return await api_createItemReplacement(this.backendClient, itemId, versionDto);
  }

  async deleteItem(itemId: string, immediate: boolean) {
    await api_deleteItem(this.backendClient, itemId, immediate);
  }

  async updateOrCreateKeldaStatus(configId: string, updateOrCreateDto: UpdateOrCreateKeldaStatusDto) {
    return await api_updateOrCreateKeldaStatus(this.backendClient, configId, updateOrCreateDto);
  }

  async deleteFolder(folderId: string) {
    await api_deleteFolder(this.backendClient, folderId);
  }

  async getKeldaWorkerConfig(id: string) {
    return await api_getKeldaWorkerConfig(this.backendClient, id);
  }

  async getTenantConfig(additionalParams?: AdditionalParams) {
    return await api_getTenantConfig(this.backendClient, additionalParams);
  }

  async getKeldaSupervisorConfig(
    id: string,
    keldaVersion: string,
    hostname: string,
    keldaInstanceId: string,
    cpuArch: 'aarch64' | 'x86_64',
  ) {
    return await api_getKeldaSupervisorConfig(this.backendClient, id, keldaVersion, hostname, keldaInstanceId, cpuArch);
  }

  async getPackages(id: string, keldaVersion: string | undefined) {
    return await api_getPackages(this.backendClient, id, keldaVersion);
  }

  async getPackagesFromUrl(packageListUrl: string) {
    const response = await apiGet(this.backendClient.getApiClient(), packageListUrl);
    return assumeResultArray<PackageDefinition>(response);
  }

  async getOfflineStoragePluginsForKelda(keldaId: string) {
    return await api_getOfflineStoragePluginsForIntegrator(this.backendClient, keldaId);
  }

  async postRecordLog(body: any) {
    return await api_postRecordLog(this.backendClient, body);
  }

  async postMosMessage(keldaId: string, body: MosRequestFromNcsDto | MosResponseFromNcsDto | MosLockReleaseRequestDto) {
    return await api_postMosMessage(this.backendClient, keldaId, body);
  }

  async getNextMosMessage(keldaId: string, queue: 'lower_port' | 'upper_port' | 'port_10542', workerId: string) {
    return await api_getNextMosMessage(this.backendClient, keldaId, queue, workerId);
  }

  async updateTimedMetadata(itemId: string, update: UpdateTimedMetadataRequestDto) {
    return api_updateTimedMetadata(this.backendClient, itemId, update);
  }

  async startBackendJob(payload: StartAnalyticsJobRequestDto) {
    await api_startBackendJob(this.backendClient, payload);
  }

  async updateArchiveStatus(itemId: string, payload: ArchiveOperationRequestDto) {
    return await api_updateArchiveStatus(this.backendClient, itemId, payload);
  }

  async getMediaSegmentUrls(itemId: string, streamIds: StreamRoleId[], signedForMethod: 'get' | 'put', firstSegmentIndex: number) {
    return await api_getMediaSegmentUrls(this.backendClient, itemId, streamIds, signedForMethod, firstSegmentIndex);
  }

  async markTransferRequest(storageId: string, transferRequestId: string, requestDto: MarkTransferRequestDto) {
    return await api_markTransferRequest(this.backendClient, storageId, transferRequestId, requestDto);
  }

  async getRouter(routerId: string) {
    return await api_getRouter(this.backendClient, routerId);
  }

  async getRouterSources(routerId: string) {
    return await api_getRouterSources(this.backendClient, routerId);
  }

  async postRouterMessage(routerId: string, msg: RouterMessageDto) {
    await api_postRouterMessage(this.backendClient, routerId, msg);
  }

  async getExpectedLivePipelines() {
    return await api_getExpectedLivePipelines(this.backendClient);
  }

  async addItemToFolder(folderId: string, itemId: string, parentIdToRemove?: string | undefined) {
    await api_addItemToFolder(this.backendClient, folderId, itemId, parentIdToRemove);
  }

  async reportPipelineActionResult(itemId: string, dto: PipelineActionResultDto) {
    await api_reportPipelineActionResult(this.backendClient, itemId, dto);
  }

  async getExternalHighresInfo(itemId: string): Promise<ExternalHighresInfoDto | null> {
    return await api_getExternalHighresInfo(this.backendClient, itemId);
  }

  async getPendingOfflineStorageJobs(pluginId: string) {
    return await api_getPendingOfflineStorageJobs(this.backendClient, pluginId);
  }

  async getStartedOfflineStorageJobs(pluginId: string) {
    return await api_getStartedOfflineStorageJobs(this.backendClient, pluginId);
  }

  async updateOfflineStorageJob(jobId: string, update: OfflineStorageJobUpdate) {
    await api_updateOfflineStorageJob(this.backendClient, jobId, update);
  }

  async appendRetrievedSegment(itemId: string, dto: RetrievedSegmentRangeDto) {
    await api_appendRetrievedSegment(this.backendClient, itemId, dto);
  }

  async logStatistic(body: LogStaticRequestDto) {
    await api_logStatistic(this.backendClient, body);
  }

  async undeleteItem(itemId: string) {
    await api_undeleteItem(this.backendClient, itemId);
  }

  async getItemOfflineStorageInfo(itemId: string) {
    return api_getItemOfflineStorageInfo(this.backendClient, itemId);
  }

  async updateItemRelations(itemId: string, relationType: RelationType, relatedItemIds: string[]): Promise<void> {
    await api_updateItemRelations(this.backendClient, itemId, relationType, relatedItemIds);
  }

  async getItemRelations(itemId: string, params?: GetItemRelationsParams): Promise<Relation[]> {
    return api_getItemRelations(this.backendClient, itemId, params);
  }

  async getScheduledRecording(itemId: string) {
    return api_getScheduledRecording(this.backendClient, itemId);
  }

  async getAllItemAttachments(itemId: string): Promise<ItemAttachmentWithDownloadUrlDto[]> {
    return await api_getAttachmentsToItem(this.backendClient, itemId);
  }

  async getTimedMetadata(itemId: string): Promise<TimedMetadataCollection> {
    return await api_getTimedMetadata(this.backendClient, itemId);
  }

  async getItem(itemId: string, queryParams?: HttpQueryParameters) {
    return api_getItem(this.backendClient, itemId, { queryParams });
  }

  async postQcEvents(itemId: string, qcEvents: QcEvent[]) {
    return api_postQcEvents(this.backendClient, itemId, { events: qcEvents });
  }

  async getItemAncestry(itemId: string) {
    return await api_getItemAncestry(this.backendClient, itemId, undefined);
  }

  async getFoldersAndItems(folderId: string, from = 0) {
    const results = await api_getFolderContent(this.backendClient, folderId, {
      from,
      itemsPerPage: 100,
    });
    return results as SearchResult;
  }

  async getFolders(folderId: string, from = 0) {
    const results = await api_getFolderContent(this.backendClient, folderId, {
      from,
      itemsPerPage: 100,
      type: FolderTypeName,
    });
    return results as FolderDto[];
  }

  async getFolder(folderId: string): Promise<FolderDto> {
    return await callApiCommand(this.backendClient, new GetFolderCommand({ folderId }), undefined);
  }

  async createFolder(parentFolderId: string, foldername: string) {
    return await api_createFolder(this.backendClient, foldername, parentFolderId, false);
  }

  async search(searchQuery: SearchProps): Promise<SearchResult> {
    const params = buildSearchParams(searchQuery);
    return await getSearchResult(this.backendClient, params);
  }

  async deleteItemUploadLock(itemId: string) {
    await api_deleteItemUploadLock(this.backendClient, itemId);
  }

  async getOptionList(optionListId: string): Promise<OptionListDto | undefined> {
    try {
      const response = await apiGet(this.backendClient.getApiClient(), ofOptionList(this.backendClient.getServiceLinks(), optionListId));
      return assumeResultObject<OptionListDto>(response);
    } catch (e) {
      console.log(`Error getting option list for id ${optionListId}`, e);
    }
    return undefined;
  }

  async createItem(
    filename: string,
    itemType: DynamoItemType | undefined,
    parentFolderId: string,
    placeholder: boolean,
    analyticConfig: AnalyticConfig,
    editProxyIngested: boolean,
    enabledStreams: StreamRoleId[],
    mdfData: ItemMetaData | undefined,
    makeCleanCopy: boolean,
    progressInfo: ProgressInfo | undefined,
    visibleTo: ItemAccessControl | undefined,
    updatableBy: ItemAccessControl | undefined,
    locationData: GeoShape | undefined,
    generatedMediaLocationId: string | undefined,
    timecode: string | undefined,
  ): Promise<ItemCreationResponse> {
    const metadata = {
      formId: mdfData?.formId ?? DEFAULT_MDF_ID,
      formData: {
        [SystemDefaultField.CreatedOn]: new Date().toISOString(),
        [SystemDefaultField.Title]: filename,
        ...(mdfData?.formData && { ...mdfData.formData }),
      }
    };

    const body: ItemCreationRequestDto = {
      itemType: itemType || itemTypeFromFilename(filename),
      folderParents: parentFolderId ? [parentFolderId] : null,
      metadata,
      editProxyIngested,
      title: filename,
      filename: filename,
      fileType: mediaTypeFromFilename(filename),
      executeSameActionOnArchiveCopy: makeCleanCopy,
      progressInfo: progressInfo,
      ...(convertAnalyticConfig(analyticConfig)),
      visibleTo,
      updatableBy,
      enabledStreams,
      generatedMediaLocationId,
      timecode,
    };

    const additionalParams: AdditionalParams = {
      queryParams: {
        [READABLE_MDF_FIELDS_QUERY_PARAMETER]: false,
      }
    };
    let result = await apiPost(this.backendClient.getApiClient(), this.backendClient.getServiceLinks().items, body, additionalParams);

    const itemId = assumeResultObject<ItemCreationResponse>(result).id;
    if (locationData && itemId) {
      try {
        const itemUrl = ofItem(this.backendClient.getServiceLinks(), itemId);
        await apiPost(this.backendClient.getApiClient(), itemUrl, { geoLocation: locationData });
      } catch(e) {
        console.log('Something went wrong while updating item geoLocation');
      }
    }
    if (placeholder && itemId) {
      const update = {
        id: itemId,
        metadata
      };

      try {
        const itemUrl = ofItem(this.backendClient.getServiceLinks(), itemId);
        result = await apiPost(this.backendClient.getApiClient(), itemUrl, update, additionalParams);
        return assumeResultObject<ItemCreationResponse>(result);
      } catch(e) {
        console.log('Something went wrong');
      }
    }
    return assumeResultObject<ItemCreationResponse>(result);
  }

  async batchGetItemsByExternalId(dto: BatchGetItemsByExternalIdRequestDto) {
    return await api_batchGetItemsByExternalId(this.backendClient, dto);
  }

  async batchCreateItems(dto: BatchCreateItemsRequestDto, passive?: boolean) {
    return await api_batchCreateItems(this.backendClient, dto, {queryParams: {passive}});
  }

  async batchUpdateItems(dto: BatchUpdateItemsRequestDto, passive?: boolean) {
    return await api_batchUpdateItems(this.backendClient, dto, {queryParams: {passive}});
  }

  async batchSignUploadURLs(dto: BatchSignJobUploadUrlsInput, keldaRequestId: string, highresUploadLocationId?: string) {
    return await api_batchSignTaskUploadURLs(this.backendClient, dto, keldaRequestId, highresUploadLocationId);
  }

  async batchConcludeJobUploads(dto: BatchConcludeJobUploadInput, keldaRequestId: string, highresUploadLocationId?: string, passive?: boolean): Promise<BatchConcludeJobUploadResponse> {
    return await api_batchConcludeJobUploadURLs(this.backendClient, dto, keldaRequestId, highresUploadLocationId, passive);
  }

  async getItems(ids: string[]): Promise<GetItemsDto[]> {
    return await api_getItems(this.backendClient, ids);
  }

  async createGeneratedMediaUploadUrl(itemId: string, type: 'exif' | 'thumbnail' | 'aaf'): Promise<GeneratedMediaSignedUrlResponseDto> {
    return await api_createGeneratedMediaUploadUrl(this.backendClient, itemId, type);
  }

  async performLiveItemAction(itemId: string, dto: LiveItemStateRequestDto): Promise<LiveRecordingItemStateDto> {
    return await api_performLiveItemAction(this.backendClient, itemId, dto);
  }

  async getMediaPipelineCrosspoint(liveSourceId: string, pipelineId: string): Promise<string | null> {
    const dto = await api_getSelectedDynamicSourceForPipeline(this.backendClient, liveSourceId, pipelineId);
    return dto.source;
  }

  async setMediaPipelineCrosspoint(liveSourceId: string, pipelineId: string, payload: UpdateSelectedDynamicSourceDto): Promise<void> {
    await api_setSelectedDynamicSourceForPipeline(this.backendClient, liveSourceId, pipelineId, payload);
  }

  async updateKeldaMediaPipelineStatus(liveSourceId: string, pipelineId: string, payload: UpdateKeldaMediaPipelineStatusDto): Promise<void> {
    await api_updateKeldaMediaPipelineStatus(this.backendClient, liveSourceId, pipelineId, payload);
  }

  async getExifEmbeddedUrl(url: string, fileName: string, itemId: string): Promise<GetExifEmbeddedUrlResponse> {
    const payload = {
      url,
      fileName,
      itemId
    };

    const response = await sendApiCommand(
      this.backendClient,
      new GetEXIFEmbeddedImageURLCommand(payload)
    );

    return response;
  }
}

export function backendOpsMetadataUpdater(
  backendOps: BackendOps,
  logger: Logger
): ItemMetadataUpdater {
  return new ItemMetadataUpdater({
    getMetadata(itemId: string) {
      return backendOps.getItemFields(itemId, ['metadata', 'metadataVersion']);
    },
    async updateMetadata(itemId, payload) {
      const updated = await backendOps.updateItem(itemId, payload);
      const { metadata, metadataVersion } = updated;
      invariant(metadata, 'updateMetadata: Expecting metadata in response');
      invariant(metadataVersion, 'updateMetadata: Expecting metadataVersion in response');
      return { metadata, metadataVersion };
    },

  }, logger);
}
